/**
 * Federal Poverty Guidelines threshold for the I-864 Affidavit of
 * Support.
 *
 * USCIS Form I-864P republishes the HHS Federal Poverty Guidelines
 * with the multipliers that apply to I-864 sponsors:
 *   - 100% FPG: active-duty military sponsoring spouse/child
 *   - 125% FPG: every other I-864 sponsor (default)
 *
 * This app only supports the 125% path today (no military toggle in
 * the screener). Values below are the 2025 HHS table, contiguous-US
 * 48 + DC, Alaska, and Hawaii — 125% column only.
 *
 * ANNUAL UPDATE REQUIRED: HHS publishes new FPG figures in January
 * each year; USCIS adopts them via a revised Form I-864P, typically
 * effective around April. If `POVERTY_GUIDELINES_EDITION` lags the
 * current year, the asset auto-skip threshold is stale and sponsors
 * with borderline income could be told they don't need Part 7 when
 * they actually do. Refresh from:
 *   https://www.uscis.gov/i-864p
 */
export const POVERTY_GUIDELINES_EDITION = '2025';

export type TPovertyRegion = 'contiguous' | 'alaska' | 'hawaii';

/**
 * 2025 HHS Federal Poverty Guidelines (125%), in whole dollars.
 * Source: USCIS Form I-864P, effective March 1, 2025.
 */
const FPG_125_2025: Readonly<Record<TPovertyRegion, {
  bySize: Readonly<Record<number, number>>;
  perAdditional: number;
}>> = {
  contiguous: {
    bySize: { 1: 19562, 2: 26437, 3: 33312, 4: 40187, 5: 47062, 6: 53937, 7: 60812, 8: 67687 },
    perAdditional: 6875,
  },
  alaska: {
    bySize: { 1: 24450, 2: 33062, 3: 41675, 4: 50287, 5: 58900, 6: 67512, 7: 76125, 8: 84737 },
    perAdditional: 8612,
  },
  hawaii: {
    bySize: { 1: 22500, 2: 30425, 3: 38350, 4: 46275, 5: 54200, 6: 62125, 7: 70050, 8: 77975 },
    perAdditional: 7925,
  },
};

/**
 * USPS two-letter state code → poverty region. Alaska and Hawaii
 * have higher FPG schedules per HHS; the 48 contiguous states + DC
 * + territories all use the contiguous table.
 */
export function getPovertyRegionForState(
  state: string | undefined,
): TPovertyRegion {
  if (state === 'AK') return 'alaska';
  if (state === 'HI') return 'hawaii';
  return 'contiguous';
}

/**
 * Returns the 125% poverty threshold for a household of `householdSize`
 * in `region` (defaults to contiguous-US). Household sizes ≤ 0 return
 * 0 — callers should handle that as "unknown threshold" and not
 * auto-skip Part 7.
 *
 * Sizes above 8 extrapolate linearly via the published per-additional
 * adjustment, matching the I-864P note "Add $X for each additional
 * person."
 */
export function compute125PovertyThreshold(
  householdSize: number,
  region: TPovertyRegion = 'contiguous',
): number {
  if (!Number.isFinite(householdSize) || householdSize <= 0) return 0;
  const size = Math.floor(householdSize);
  const table = FPG_125_2025[region];
  const eightCap = table.bySize[8];
  if (eightCap === undefined) return 0;
  if (size <= 8) return table.bySize[size] ?? 0;
  return eightCap + (size - 8) * table.perAdditional;
}

/**
 * True when `income` meets or exceeds 125% FPG for the household.
 * Returns false on invalid inputs (zero / negative size). Callers
 * use this to auto-skip Part 7 (assets) and the household co-filer
 * gate when the sponsor's income alone clears the threshold.
 */
export function meetsPovertyThreshold(
  income: number,
  householdSize: number,
  region: TPovertyRegion = 'contiguous',
): boolean {
  if (!Number.isFinite(income) || income <= 0) return false;
  const threshold = compute125PovertyThreshold(householdSize, region);
  if (threshold <= 0) return false;
  return income >= threshold;
}
